---
title: "How to set up WhatsApp outreach"
description: "Connect a Meta WhatsApp Business number, sync templates, run campaigns and use the shared Inbox inside the 24-hour window."
publishedAt: "2026-08-14"
updatedAt: "2026-10-25"
slug: "how-to-set-up-whatsapp-outreach"
---

# How to set up WhatsApp outreach

WhatsApp outreach lets you message contacts from the same list you already manage in NerdCRM, with replies landing in the **Inbox**, a shared view your whole team can work from.

It's a paid feature. The free plan includes **100 outbound messages** to try it — see [your free allowance](/help/free-allowance-and-upgrading).

An admin sets it up in **Settings → WhatsApp**. Once it's on, members can run their own campaigns and answer their own contacts' conversations.

## Before you start

You need a Meta (Facebook) Business account, a WhatsApp Business Account (WABA), and a phone number that is **not** currently registered to the WhatsApp or WhatsApp Business app. If the number is in use, delete that account first — Meta won't let you register a number that already has one.

## Step 1 — Create the Meta app

1. In the Meta developer console, create an app and add the **WhatsApp** product.
2. Note the **App ID** and **App secret** (Settings → Basic).
3. Add your phone number under the WhatsApp product and complete verification. Note its **Phone Number ID** and your **WABA ID**.
4. Create a **System User** in Meta Business Settings and generate a long-lived **access token** for it with the `whatsapp_business_messaging` and `whatsapp_business_management` permissions. A personal token expires within a day, and your sends would stop silently.

## Step 2 — Connect the app to NerdCRM

Go to **Settings → WhatsApp**. In the **Meta app connection** card, enter:

- **Meta App ID** and **App secret** — the app secret is encrypted at rest and never shown again.
- **Verify token** — any random string you choose. You'll paste the same value into Meta.

Turn **WhatsApp enabled** on (the switch at the top of the page) and click **Save changes**. Save before you verify the webhook in Meta, because NerdCRM needs the app secret to check every message Meta sends. The card shows **Configured** once the App ID and app secret are saved.

## Step 3 — Point Meta's webhook at NerdCRM

In the Meta console, set the webhook callback URL to the **Callback URL** shown in the same card (there's a **Copy** button) and paste your verify token. Subscribe to the `messages` field.

Meta calls the URL once to verify it. If verification fails, the token doesn't match exactly — check for trailing spaces.

## Step 4 — Register the number and subscribe the app

Meta's dashboard doesn't do these two things for you. They are Cloud API calls, and skipping either is the most common reason a first send or the first reply fails. Make both calls in Meta's Graph API Explorer, with your app selected and the System User token from step 1:

1. **Register the number.** Send a `POST` to `{phone-number-id}/register` with `messaging_product=whatsapp` and `pin=` a 6-digit PIN you choose. Write the PIN down; you may need it if the number is ever re-registered. A `{"success": true}` answer, or an error saying the number is already registered, means you're done.
2. **Subscribe the app to your WhatsApp Business Account.** Send a `POST` to `{waba-id}/subscribed_apps` with no body. Then send a `GET` to the same path to check that your app now appears in the list.

Do both for every number and every WhatsApp Business Account you use.

## Step 5 — Add your number

Under **Sending numbers**, click **Add number** and fill in:

- **WABA ID** and **Phone Number ID** — from step 1
- **Access Token** — the System User token from step 1 (encrypted, never shown again)
- **Display Name** and **Display Phone** — how the number appears in NerdCRM
- **Default Country Code** — digits only, no `+` (for example `880`). It's used to complete local numbers

Turn the number's **Enabled** switch on and click **Add Number**. The optional **Pacing & Safety** settings (send window, daily cap, warm-up ramp and send interval) slow the sending down to protect your number's quality rating; you can change them later with **Edit limits** on the number's card.

## Step 6 — Sync your templates

WhatsApp only lets you start a conversation with a **template** that Meta has approved in advance. Under **Message templates**, choose your number (if you have more than one) and click **New template** to write a text template, then **Submit to Meta** for approval. Media-header and carousel templates still have to be made in the Meta console.

Templates take anywhere from minutes to a day to be approved. Rejections are usually promotional language or a missing variable example. Once a template is approved, click **Sync from Meta** to pull it in with its variables. The list shows each template's status.

## Step 7 — Run a campaign

Open **Campaigns** and click **New campaign**. If your organization also sends email campaigns, choose **WhatsApp** from the menu. In the builder:

1. Name the campaign and pick the **Target tag** — everyone carrying that tag is your audience.
2. Pick the **Sender** (your registered number) and a **Throttle** in messages per minute.
3. In the sequence on the left, select a step, pick an approved **Template** and map its variables to contact fields. Click **+ Add step** to follow up, and set each step's **Delay (days)**.
4. Click **Create** to save the draft. The **Audience** card shows how many contacts carry the tag, how many have no WhatsApp number, how many are opted out or in cooldown, and how many **Will receive** the message.
5. Click **Review & launch**. The summary lists the audience, who will receive it, the sender and the sequence, and warns you if the audience is larger than your remaining free allowance. Click **Send now** to start.

Use **Pause** and **Resume** on the campaign to stop and restart sending.

## The 24-hour window

This is the rule that trips people up most.

- To **start** a conversation you must use an approved template.
- Once a contact replies, a **24-hour customer service window** opens. Inside it you can send free-form messages.
- When the window closes, you're back to templates only.

Replies arrive in the **Inbox**. Open the **WhatsApp** tab, or turn on **Needs reply**, to see who is waiting. Every conversation shows its window, so you can see at a glance whether you can still reply freely: *Window open · 5h left*, *Window closes in 2h* or *Window closed · template only*. While the window is open the reply box reads *Reply on WhatsApp*. When it closes, it switches to *24h window closed — send a template*: pick an approved template and click **Send template**.

## Opt-outs

Anyone who asks to stop is added to your suppression list and excluded from every future campaign automatically. Respect it — WhatsApp is far less forgiving than email, and a pattern of unwanted messages can get your number restricted by Meta.

Opt-outs are per channel: replying STOP on WhatsApp doesn't stop email, and unsubscribing from email doesn't stop WhatsApp.

## Troubleshooting

| Symptom | Likely cause |
|---|---|
| Campaign paused on its own | Free allowance used up — the campaign resumes after you upgrade and click **Resume** |
| Template send rejected | Template not approved, or a variable is missing a value |
| Can't send free-form text | The 24-hour window has closed; use a template |
| Sends fail with "Account not registered" | The number isn't registered with the WhatsApp Cloud API yet. Do step 4, part 1 |
| Nothing arrives in the Inbox | The Meta webhook isn't verified, `messages` isn't subscribed, or your app isn't subscribed to your WhatsApp Business Account (step 4, part 2) |
| Members can't see **Campaigns** or **Inbox** | **WhatsApp enabled** is off in **Settings → WhatsApp** (read-only users never see them) |
